import React, { ComponentProps, forwardRef, useMemo, JSX } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Colors } from '@surglogs-app/ui.theme'

import { TypographyColorVariant } from '../services/TypographyService'

import { Text } from './Text'

const getHoverColor = (variant?: TypographyColorVariant): Colors => {
  if (variant === 'default') {
    return 'blue.100'
  }

  return 'blue.75'
}

const StyledText = styled(Text, {
  shouldForwardProp: (prop) => !['underlined'].includes(prop),
})<{
  disabled?: boolean
  underlined?: boolean
}>`
  cursor: pointer;
  background: transparent;
  text-decoration: ${({ underlined }) => (underlined ? 'underline' : 'none')};
  outline: none;
  box-shadow: none;
  text-align: ${({ ta }) => (ta ? ta : 'start')};

  &:hover {
    color: ${({ theme, variant }) => theme.ui.colors[getHoverColor(variant)]};
  }

  ${({ theme, disabled }) =>
    disabled &&
    css`
      cursor: default;
      user-select: none;
      color: ${theme.ui.colors['navy.25']};

      // disable click in disabled state (required when it's anchor element)
      &:active {
        pointer-events: none;
      }

      &:hover {
        text-decoration: none;
        color: ${theme.ui.colors['navy.10']};
      }
    `};
`

type AnchorProps = {
  as: 'a'
  // require href when as is "a"
  href: string
}

type ButtonProps = {
  as?: Exclude<React.ElementType, 'a'>
}

type AdditionalProps = AnchorProps | ButtonProps

type TextButtonProps = Omit<
  ComponentProps<typeof StyledText>,
  'paragraph' | 'as'
> &
  AdditionalProps & {
    type?: JSX.IntrinsicElements['button']['type']
    /** Decides whether button should be underlined */
    underlined?: boolean
  }

export const TextButton = forwardRef<
  HTMLSpanElement | HTMLParagraphElement,
  TextButtonProps
>(
  (
    {
      as,
      disabled,
      variant = 'primary',
      bold = true,
      italic = false,
      role,
      type,
      underlined = false,
      padding = 'abs.0',
      bd = 'none',
      ...rest
    },
    ref,
  ) => {
    const isAnchor = as === 'a'
    const href = isAnchor ? (rest as AnchorProps).href : undefined

    const textButtonRole = useMemo(() => {
      if (role) {
        return role
      }

      return !isAnchor ? 'button' : undefined
    }, [isAnchor, role])

    const textButtonType = useMemo(() => {
      if (type) {
        return type
      }

      return !isAnchor ? 'button' : undefined
    }, [isAnchor, type])

    return (
      <StyledText
        {...{
          ref,
          variant,
          bold,
          disabled,
          italic,
          href,
          as: as ?? 'button',
          role: textButtonRole,
          type: textButtonType,
          underlined,
          padding,
          bd,
          ...rest,
        }}
      />
    )
  },
)
